'use client';

import { useState } from 'react';
import {
  trackOverdraftCalculatorCalculate,
  trackOverdraftCalculatorFrequency,
  trackOverdraftCalculatorReset,
} from 'components/analytics/eventHandlerTracking';
import Link from 'components/shared/Link/Link';
import { CalculatorShell } from 'components/Widget/shared/CalculatorShell';
import { deferTracking } from 'components/Widget/shared/deferTracking';
import { usePanelSwapFocus } from 'components/Widget/shared/usePanelSwapFocus';
import { BANKRATE_URL, DEFAULT_COUNT, DEFAULT_FREQUENCY, DISCLAIMER, TITLE } from './constants';
import { calculateAnnualSavings, formatAnnualSavings } from './overdraft';
import { OverdraftCalculatorForm } from './OverdraftCalculatorForm';
import { OverdraftCalculatorResult } from './OverdraftCalculatorResult';
import { OverdraftCalculatorSummary } from './OverdraftCalculatorSummary';
import type { FrequencyKey, FrequencyOption } from './types';

export const OverdraftCalculator = () => {
  const [count, setCount] = useState(DEFAULT_COUNT);
  const [frequency, setFrequency] = useState<FrequencyKey>(DEFAULT_FREQUENCY);
  const [showResult, setShowResult] = useState(false);
  const { formRef, summaryRef, markPanelSwap } = usePanelSwapFocus(showResult);
  // Derived rather than stored: the form is gone while the result shows, so its inputs cannot drift.
  const savings = showResult ? formatAnnualSavings(calculateAnnualSavings(count, frequency)) : null;

  const handleCalculate = () => {
    setShowResult(true);
    markPanelSwap();

    deferTracking(() => {
      trackOverdraftCalculatorCalculate();
    });
  };

  /** Legacy closed its popup back onto the form with the slider and frequency untouched. */
  const handleStartOver = () => {
    setShowResult(false);
    markPanelSwap();

    deferTracking(() => {
      trackOverdraftCalculatorReset();
    });
  };

  // Takes the whole option so the analytics label is the visible text legacy reported, without a lookup.
  const handleFrequencyChange = (option: FrequencyOption) => {
    setFrequency(option.key);

    deferTracking(() => {
      trackOverdraftCalculatorFrequency({ label: option.label });
    });
  };

  return (
    <CalculatorShell
      title={TITLE}
      disclaimer={
        <>
          {DISCLAIMER}{' '}
          <Link href={BANKRATE_URL} className="break-words underline">
            {BANKRATE_URL}
          </Link>
        </>
      }
      summaryRef={summaryRef}
      summary={savings !== null && <OverdraftCalculatorSummary savings={savings} />}>
      {savings === null ? (
        <OverdraftCalculatorForm
          ref={formRef}
          count={count}
          frequency={frequency}
          onCountChange={setCount}
          onFrequencyChange={handleFrequencyChange}
          onCalculate={handleCalculate}
        />
      ) : (
        <OverdraftCalculatorResult onStartOver={handleStartOver} />
      )}
    </CalculatorShell>
  );
};
